import * as React from 'react';
import type { Editor } from '@tiptap/react';

import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';

import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';
import { isNodeInSchema } from 'containers/Tiptap/lib/tiptap-utils';
import { useStore } from 'utils/hooks/store';

import { ChevronDownIcon } from 'containers/Tiptap/components/tiptap-icons/chevron-down-icon';
import { ListIndentedIcon } from 'containers/Tiptap/components/tiptap-icons/list-indented-icon';
import { MinusIcon } from 'containers/Tiptap/components/tiptap-icons/minus-icon';
import { TableIcon } from 'containers/Tiptap/components/tiptap-icons/table-icon';

import { ButtonProps, Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
} from 'containers/Tiptap/components/tiptap-ui-primitive/dropdown-menu';

import { addRiskMatrix } from 'containers/Tiptap/components/tiptap-ui/risk-matrix-button/risk-matrix-button';

import {
  DEFAULT_COLUMNS as RISK_ASSESSMENT_DEFAULT_COLUMNS,
  RISK_ASSESSMENT_DEFAULTS,
} from 'containers/Tiptap/components/tiptap-node/risk-assessment-node/constants';

interface InsertItem {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  check: (editor: Editor) => boolean;
  action: (editor: Editor) => void;
}

const INSERT_ITEMS: InsertItem[] = [
  {
    id: 'risk_matrix',
    label: 'Risk matrix',
    Icon: ({ className }) => <WindowOutlinedIcon className={className} fontSize="small" />,
    check: (editor) => isNodeInSchema('riskMatrix', editor),
    action: (editor) => addRiskMatrix(editor),
  },
  {
    id: 'risk_assessment',
    label: 'Risk assessment',
    Icon: ({ className }) => <TableIcon className={className} />,
    check: (editor) => isNodeInSchema('riskAssessment', editor),
    action: (editor) =>
      editor
        .chain()
        .focus()
        .setRiskAssessmentNode({
          defaultColumns: RISK_ASSESSMENT_DEFAULT_COLUMNS,
          defaultLikelihoodLabels: RISK_ASSESSMENT_DEFAULTS.LIKELIHOOD_LABELS,
          defaultSeverityLabels: RISK_ASSESSMENT_DEFAULTS.SEVERITY_LABELS,
          editable: true,
        })
        .run(),
  },
  {
    id: 'toc',
    label: 'Table of contents',
    Icon: ({ className }) => <ListIndentedIcon className={className} />,
    check: (editor) => isNodeInSchema('tocNode', editor),
    action: (editor) => editor.chain().focus().insertTocNode().run(),
  },
  {
    id: 'divider',
    label: 'Separator',
    Icon: ({ className }) => <MinusIcon className={className} />,
    check: (editor) => isNodeInSchema('horizontalRule', editor),
    action: (editor) => editor.chain().focus().setHorizontalRule().run(),
  },
];

export interface InsertDropdownMenuProps extends Omit<ButtonProps, 'type'> {
  editor?: Editor | null;
  onOpenChange?: (isOpen: boolean) => void;
}

export function InsertDropdownMenu({
  editor: providedEditor,
  onOpenChange,
  ...props
}: InsertDropdownMenuProps): React.ReactNode {
  const [isOpen, setIsOpen] = React.useState(false);
  const { editor } = useTiptapEditor(providedEditor);
  const { session } = useStore();

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  if (!editor || !editor.isEditable) {
    return null;
  }

  const riskAssessmentEnabled = session.user?.riskAssessmentEnabled ?? false;
  const availableItems = INSERT_ITEMS.filter(
    (item) => item.check(editor) && (item.id !== 'risk_assessment' || riskAssessmentEnabled)
  );

  if (availableItems.length === 0) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          role="button"
          tabIndex={-1}
          aria-label="Insert"
          tooltip="Insert"
          {...props}
        >
          <WidgetsOutlinedIcon className="tiptap-button-icon" fontSize="small" />
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {availableItems.map(({ id, label, Icon, action }) => (
            <DropdownMenuItem
              key={id}
              asChild
              onSelect={() => {
                action(editor);
                setIsOpen(false);
              }}
            >
              <Button
                type="button"
                data-style="ghost"
                role="button"
                tabIndex={-1}
                aria-label={label}
                tooltip=""
              >
                <Icon className="tiptap-button-icon" />
                <span className="tiptap-button-text">{label}</span>
              </Button>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default InsertDropdownMenu;
